import React from 'react';
import { Link } from 'react-router-dom';
import { kmToMiles } from '../../utils/distance';

const DetailPanel = ({ species, onClose }) => {
  if (!species) return null;

  return (
    <section className="absolute left-[26rem] md:left-[30rem] top-24 bottom-24 w-80 z-20 pointer-events-none hidden lg:block animate-fadeSlideUp">
      <div className="h-full w-full pointer-events-auto bg-surface-container/70 backdrop-blur-[20px] rounded-3xl shadow-[0px_32px_64px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden">
        {/* Header with close */}
        <div className="p-6 pb-3 flex items-start justify-between">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-primary font-label">
              Species Detail
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-500 hover:text-on-surface hover:bg-white/5 transition-all"
            aria-label="Close detail panel"
          >
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-5 hide-scrollbar">
          {/* Species image + name */}
          <div className="flex items-center gap-3">
            {species.image ? (
              <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                <img src={species.image} alt={species.name} className="w-full h-full object-cover" />
              </div>
            ) : (
              <span className="text-4xl">{species.icon}</span>
            )}
            <div>
              <h3 className="font-headline text-xl text-on-surface">{species.name}</h3>
              <p className="text-xs text-slate-400 italic">{species.scientificName}</p>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-on-surface-variant leading-relaxed font-body">
            {species.description}
          </p>

          {/* Metrics grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/5 rounded-xl p-3">
              <span className="text-[9px] text-slate-500 uppercase font-label block mb-1">Distance</span>
              <span className="text-sm font-medium text-on-surface">{kmToMiles(species.distance)}</span>
            </div>
            <div className="bg-white/5 rounded-xl p-3">
              <span className="text-[9px] text-slate-500 uppercase font-label block mb-1">Status</span>
              <span className={`text-sm font-medium ${
                species.status === 'Least Concern' ? 'text-secondary' :
                species.status === 'Endangered' || species.status === 'Critically Endangered' ? 'text-error' :
                'text-primary'
              }`}>{species.status}</span>
            </div>
            <div className="bg-white/5 rounded-xl p-3">
              <span className="text-[9px] text-slate-500 uppercase font-label block mb-1">Departs</span>
              <span className="text-sm font-medium text-on-surface">{species.seasons.depart}</span>
            </div>
            <div className="bg-white/5 rounded-xl p-3">
              <span className="text-[9px] text-slate-500 uppercase font-label block mb-1">Arrives</span>
              <span className="text-sm font-medium text-on-surface">{species.seasons.arrive}</span>
            </div>
          </div>

          {/* Wildlife Tip */}
          <div className="bg-primary/5 rounded-xl p-4">
            <h4 className="text-xs font-bold uppercase tracking-widest text-primary mb-2 font-label">
              Wildlife Tip
            </h4>
            <p className="text-xs text-on-surface-variant leading-relaxed font-body">
              {species.tips}
            </p>
          </div>

          {/* Migration Route */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 font-label">
              Migration Route
            </h4>
            <div className="space-y-2">
              {species.route.map((point, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                    <span className="text-[9px] font-label text-slate-400">{i + 1}</span>
                  </div>
                  <span className="text-[11px] text-on-surface-variant font-body">{point.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* View Full Profile CTA */}
          <Link
            to={`/species/${species.id}`}
            className="w-full py-3 px-4 bg-gradient-to-r from-primary to-primary-container text-on-primary font-label font-semibold text-xs rounded-xl flex items-center justify-center gap-2 hover:brightness-110 transition-all"
          >
            View Full Profile
            <span className="material-symbols-outlined text-sm">north_east</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default DetailPanel;
